import { useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardCopy, Download, Layers, Loader2 } from "lucide-react";
import { extractUrls } from "@affiliate/links-text";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { convertedLinks, toCsv } from "@/lib/bulk-export";
import { useClipboard } from "@/lib/hooks/useClipboard";
import { callErrorMessage, convertBulk, type BulkResponse } from "@/lib/hooks/useConvertLink";
import { BulkResultTable } from "./BulkResultTable";
import { ShortenToggle } from "./ShortenToggle";

const MAX_URLS = 30;
const MAX_TEXT_LENGTH = 20_000;

/** Estado e envio do lote para a edge convert-link. */
export function useBulkConversion() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<BulkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (text: string, shorten: boolean, foundCount: number): Promise<void> => {
    setError(null);
    setResponse(null);
    if (foundCount === 0) {
      setError("Nenhum link encontrado no texto. Cole um post ou uma lista com links.");
      return;
    }
    setLoading(true);
    try {
      setResponse(await convertBulk(text, shorten));
      await queryClient.invalidateQueries({ queryKey: ["link-history"] });
    } catch (callError) {
      setError(callErrorMessage(callError));
    } finally {
      setLoading(false);
    }
  };

  return { loading, response, error, setError, run };
}

interface BulkFormProps {
  readonly loading: boolean;
  readonly onSubmit: (text: string, shorten: boolean, foundCount: number) => void;
}

/** Formulario do lote com contagem ao vivo dos links encontrados. */
export function BulkForm({ loading, onSubmit }: BulkFormProps): JSX.Element {
  const [text, setText] = useState("");
  const [shorten, setShorten] = useState(true);
  const foundCount = useMemo(() => new Set(extractUrls(text)).size, [text]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit(text, shorten, foundCount);
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <Label htmlFor="bulk-text" variant="field">Post ou lista</Label>
      <textarea id="bulk-text" value={text} onChange={(event) => setText(event.target.value)} maxLength={MAX_TEXT_LENGTH} rows={8}
        placeholder={"Oferta do dia!\nhttps://amzn.to/...\nhttps://s.shopee.com.br/..."}
        className="w-full rounded-lg border border-[hsl(var(--border-strong))] bg-background/60 px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {foundCount} {foundCount === 1 ? "link encontrado" : "links encontrados"}
          {foundCount > MAX_URLS ? ` (os ${foundCount - MAX_URLS} excedentes serão ignorados)` : ""}
        </p>
        <ShortenToggle checked={shorten} onChange={setShorten} />
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Layers size={15} />}
        {loading ? "Convertendo..." : "Converter links"}
      </Button>
    </form>
  );
}

function downloadCsv(response: BulkResponse): void {
  const blob = new Blob(["﻿" + toCsv(response.items)], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = `links-em-massa-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(href);
}

interface BulkResultSectionProps {
  readonly response: BulkResponse;
  readonly onCopyError: (message: string) => void;
}

/** Resumo, copiar todos, exportar CSV e tabela por linha. */
export function BulkResultSection({ response, onCopyError }: BulkResultSectionProps): JSX.Element {
  const { copiedKey, copy } = useClipboard();
  const { summary } = response;

  const handleCopyAll = async (): Promise<void> => {
    if (!(await copy(convertedLinks(response.items)))) {
      onCopyError("Não foi possível copiar. Use Exportar CSV.");
    }
  };

  return (
    <section className="space-y-3" aria-labelledby="bulk-result-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="bulk-result-title" className="text-sm font-semibold text-foreground">
          {summary.converted} convertidos · {summary.errors} com erro · {summary.ignored} ignorados
        </h2>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={() => void handleCopyAll()} disabled={summary.converted === 0}>
            {copiedKey ? <Check size={14} /> : <ClipboardCopy size={14} />}
            {copiedKey ? "Copiados" : "Copiar todos"}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => downloadCsv(response)}>
            <Download size={14} />
            Exportar CSV
          </Button>
        </div>
      </div>
      <BulkResultTable items={response.items} />
    </section>
  );
}
